import { useEffect, useRef } from 'react';
import { issueEventsTicket } from '@/api/pipeline';
import config from '@/lib/config';
import { CrmEventStream, type CrmEvent } from '@/lib/crmEvents';
import { useProjectStore } from '@/store/project';

/** Один поток событий CRM на приложение (lib/crmEvents.ts). */
export const crmEvents = new CrmEventStream({
  ticket: async () => (await issueEventsTicket()).ticket,
  url: (ticket) => `${config.CRM_API_URL}/api/crm/events?ticket=${encodeURIComponent(ticket)}`,
  open: (url) => new EventSource(url),
});

// Пропуск выдаётся под проект (область видимости CRM своя у каждого) — сменили проект, переподключаемся.
// Вернулись в приложение — поток мог умереть в фоне, переподключаемся сразу, не дожидаясь паузы.
if (typeof window !== 'undefined') {
  let project = useProjectStore.getState().activeProjectId;
  useProjectStore.subscribe((s) => {
    if (s.activeProjectId === project) return;
    project = s.activeProjectId;
    crmEvents.reconnectNow();
  });
  const wake = () => {
    if (document.visibilityState === 'visible' && crmEvents.state !== 'open') crmEvents.reconnectNow();
  };
  document.addEventListener('visibilitychange', wake);
  window.addEventListener('online', wake);
}

/** Подписка вне React: возвращает отписку. Поток один на приложение. */
export function subscribeCrmEvents(types: readonly string[], handler: (e: CrmEvent) => void): () => void {
  return crmEvents.subscribe(types, handler);
}

/**
 * Слушать события CRM, пока экран на месте. Обработчик берётся свежий на каждой отрисовке —
 * переподписка из-за него не нужна. Приходит и {type:'resync'} — поток восстановлен, перечитайте данные.
 */
export function useCrmEvents(types: readonly string[], handler: (e: CrmEvent) => void, enabled = true) {
  const ref = useRef(handler);
  ref.current = handler;
  const key = types.join(',');
  useEffect(() => {
    if (!enabled) return;
    return crmEvents.subscribe(key.split(','), (e) => ref.current(e));
  }, [key, enabled]);
}
